import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type FC,
  type ReactNode
} from "react";

type ContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
};

const GlobalSearchContext = createContext<ContextValue | null>(null);

/** Cmd+K on macOS, Ctrl+K elsewhere. */
export const isSearchShortcut = (event: KeyboardEvent): boolean =>
  (event.metaKey || event.ctrlKey) &&
  !event.altKey &&
  !event.shiftKey &&
  event.key.toLowerCase() === "k";

/** Holds the palette's open state and binds the global keyboard shortcut. */
export const GlobalSearchProvider: FC<{ children: ReactNode }> = ({
  children
}) => {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen((v) => !v), []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isSearchShortcut(event)) return;
      event.preventDefault();
      toggle();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [toggle]);

  const value = useMemo(() => ({ open, setOpen, toggle }), [open, toggle]);
  return (
    <GlobalSearchContext.Provider value={value}>
      {children}
    </GlobalSearchContext.Provider>
  );
};

/** Open / close controls for the palette. No-ops outside the provider. */
export const useGlobalSearchDialog = (): ContextValue => {
  const ctx = useContext(GlobalSearchContext);
  return (
    ctx ?? {
      open: false,
      setOpen: () => undefined,
      toggle: () => undefined
    }
  );
};
